import { useId, useRef, useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { AskAssistantButton } from "@/components/chat/AskAssistantButton";
import { Button } from "@/components/design/Button";
import { Collapse } from "@/components/design/Collapse";
import type { ButtonVariant } from "@/components/design/button-classes";
import type { AssistantContextItem } from "@/lib/use-assistant-context";
import { useAgentStore } from "@/stores/agent-store";

interface Props {
  powerName: string;
  /** The live list. Frozen at consent by the page, never re-derived afterwards. */
  items: AssistantContextItem[] | null;
  /**
   * Whether `items` holds anything of the person. Only then does the notice open; otherwise the
   * non-personal head is registered and the question sent straight away.
   */
  personal: boolean;
  prompt: () => string;
  /**
   * The real brief: the answer contract the model needs, kept out of the person's own bubble
   * (AGENT_CONTEXT_MARKER). `prompt` stays the one short sentence they asked in.
   */
  hiddenContext?: () => string;
  /** Chips that replace the generated follow-ups once this answer has arrived. */
  followUps?: () => string[];
  label: ReactNode;
  /** True once this page's consent has been given; the gate then stays out of the way. */
  consented: boolean;
  onConsent: (items: AssistantContextItem[]) => void;
  disabled?: boolean;
  /**
   * `start` is the sidebar: a full-width column, the notice capped at a readable measure.
   * `center` is the result page: the trigger sits in a centred row with [children] beside it,
   * and the notice spans exactly that row - so the block shrink-wraps to the row rather than
   * filling the page, and the Abbrechen/Fortfahren pair splits to its two ends.
   */
  align?: "start" | "center";
  /** The trigger's own look. The result page wants it to match the primary row it sits in. */
  variant?: ButtonVariant;
  /** Rendered in the same row, after the trigger. The notice below spans the whole row. */
  children?: ReactNode;
}

/**
 * The improve trigger with its consent step (spark#1029 §8; the rules are
 * @adr SPARK-0058-grow-improve-trigger-asks-before-personal-data-travels@1).
 *
 * The click no longer sends. It unfolds one sentence naming what of the person's own
 * data would go to the assistant, with Abbrechen and Fortfahren; only Fortfahren calls
 * `ask()`. No modal and no overlay — walking away decides nothing and sends nothing.
 *
 * Once consent is given the gate does not reappear: the items are registered, so a
 * second notice would promise a withholding that Abbrechen could no longer deliver.
 *
 * It opens the drawer wide: the improve answer is the one answer long enough to need it
 * (WIDE_ASSISTANT_WIDTH).
 */
export function AskWithConsent({
  powerName,
  items,
  personal,
  prompt,
  hiddenContext,
  followUps,
  label,
  consented,
  onConsent,
  disabled,
  align = "start",
  variant = "secondary",
  children,
}: Props) {
  const intl = useIntl();
  const ask = useAgentStore((s) => s.ask);
  const [open, setOpen] = useState(false);
  const noteId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Nothing personal, or already consented: the click just sends. It still registers what it sends,
  // so a turn without anything personal keeps the definition and the questionnaire state.
  const skipGate = consented || !personal;
  const centred = align === "center";

  return (
    // `inline-flex` in the centred case: the block takes the row's own width, which is what makes
    // the notice below line up with the first and last button rather than with the page.
    <div
      className={
        centred ? "inline-flex max-w-full flex-col" : "flex w-full flex-col"
      }
    >
      <div
        className={
          centred ? "flex flex-wrap items-center justify-center gap-3" : ""
        }
      >
        <AskAssistantButton
          variant={variant}
          label={label}
          disabled={disabled}
          onAsk={() => {
            if (skipGate) {
              if (!consented && items?.length) onConsent(items);
              ask(prompt(), { hiddenContext: hiddenContext?.(), followUps: followUps?.(), wide: true });
              return;
            }
            setOpen(true);
            // Focus the safe option, not the one that sends.
            requestAnimationFrame(() => cancelRef.current?.focus());
          }}
        />
        {children}
      </div>
      {/* `w-0 min-w-full` in the centred case, not `w-full`: a percentage min-width is ignored
          while the parent measures its own intrinsic width, so the notice contributes nothing to
          it and the block still shrink-wraps to the button row. With `w-full` the paragraph's
          own max-content width won and the notice ran wider than the row. */}
      <Collapse open={open} className={centred ? "w-0 min-w-full" : "w-full"}>
        {/* The shared static-hint primitive (UI_CONVENTIONS "Static Hinweis"): a non-error
            explanation in the normal flow that should be noticed, not an alert. */}
        <div className="pt-3">
          <div className={`smycles-hint ${centred ? "" : "max-w-[60ch]"}`}>
            <p id={noteId} className="smycles-field-value mt-0 text-ink">
              <FormattedMessage id="grow.askConsent.note" values={{ name: powerName }} />
            </p>
            <div
              role="group"
              aria-label={intl.formatMessage({ id: "grow.askConsent.groupAria" })}
              className={`mt-2 flex flex-wrap items-center gap-2 ${centred ? "justify-between" : ""}`}
            >
              <Button
                ref={cancelRef}
                variant="secondary"
                size="compact"
                aria-describedby={noteId}
                onClick={() => setOpen(false)}
              >
                <FormattedMessage id="common.cancel" />
              </Button>
              <Button
                variant="primary"
                size="compact"
                aria-describedby={noteId}
                onClick={() => {
                  if (items && items.length) onConsent(items);
                  setOpen(false);
                  ask(prompt(), { hiddenContext: hiddenContext?.(), followUps: followUps?.(), wide: true });
                }}
              >
                <FormattedMessage id="grow.askConsent.continue" />
              </Button>
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  );
}
